import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Table, Row, Col } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import axios from "axios";

import LocationInput from "../forms/LocationInput";
import { useBreakpoint } from "../../hooks/useWindowSize";
import config from "../../config";
import CandidatePreview from "../candidate/CandidatePreview";

const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * Defensive transform:
 * - handles missing/invalid officeSelections
 * - skips invalid selection rows
 * - adds stable `key` for AntD Table
 */
const transformCandidatesToElections = (candidates = []) => {
  const electionsMap = {};

  (Array.isArray(candidates) ? candidates : []).forEach((candidate) => {
    const selections = Array.isArray(candidate?.officeSelections)
      ? candidate.officeSelections
      : [];

    selections.forEach((selection) => {
      const district = selection?.district ?? "";
      const politicalDivision = selection?.politicalDivision ?? "";
      const state = selection?.state ?? "";

      // skip rows that don't have anything meaningful
      if (!district && !politicalDivision && !state) return;

      const key = `${district}-${politicalDivision}-${state}`;

      if (!electionsMap[key]) {
        electionsMap[key] = {
          key, // ✅ important for AntD Table row keys
          district,
          politicalDivision,
          state,
          candidates: [],
        };
      }

      electionsMap[key].candidates.push(candidate);
    });
  });

  return Object.values(electionsMap);
};

const Elections = (props) => {
  const [electionsData, setElectionsData] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [localState, setLocalState] = useState("");
  const [state, setState] = useState("");
  const [locationValue, setLocationValue] = useState("");
  const { mobileView } = useBreakpoint();

  const user = safeJSONParse(localStorage.getItem("user"), {}) || {};
  const isAdmin = user?.role === "admin";

  // Fetch candidates and build elections list (safe against CORS/502/shape issues)
  useEffect(() => {
    let mounted = true;

    const fetchCandidates = async () => {
      try {
        const response = await axios.get(
          `${config.CANDIDATES}/candidates/find-by-profileType`,
          { headers: { profileType: "candidate" } }
        );

        if (!mounted) return;

        const candidates = Array.isArray(response.data) ? response.data : [];

        let transformedElections = [];
        try {
          transformedElections = transformCandidatesToElections(candidates) || [];
        } catch (e) {
          console.warn("Transform failed (ignored):", e);
          transformedElections = [];
        }

        setElectionsData(transformedElections);
      } catch (error) {
        console.warn("Candidates fetch failed (ignored):", error?.message || error);
        if (mounted) setElectionsData([]); // ✅ safe fallback
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    // fetchCandidates();

    return () => {
      mounted = false;
    };
  }, []);

  async function getStateFromLatLng(lat, lng) {
    try {
      const { data: res } = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );

      const address = (res?.results?.[0]?.address_components || []).filter((v) =>
        (v?.types || []).includes("administrative_area_level_1")
      );

      return address?.[0]?.long_name || "";
    } catch (e) {
      console.warn("Geocode failed:", e?.message || e);
      return "";
    }
  }

  async function findCandidatesByLocation(lat, lng) {
    setIsLoading(true);
    try {
      const addressState = await getStateFromLatLng(lat, lng);
      setState(addressState);
    } finally {
      setIsLoading(false);
    }
  }

  const tableColumns = [
    { title: "District", dataIndex: "district", key: "district" },
    { title: "Political Division", dataIndex: "politicalDivision", key: "politicalDivision" },
    { title: "State", dataIndex: "state", key: "state" },
  ];

  function isRowExpanded({ expanded, onExpand, record }) {
    const showIcon = (record?.candidates?.length || 0) > 0;
    if (!showIcon) return null;

    return expanded ? (
      <Button size="small" icon={<UpOutlined />} onClick={(e) => onExpand(record, e)} />
    ) : (
      <Button size="small" icon={<DownOutlined />} onClick={(e) => onExpand(record, e)} />
    );
  }

  // Filtering (defensive)
  let filteredData = electionsData;

  if (activeFilter === "close") {
    filteredData = electionsData.filter((v) => (v?.state || "").includes(localState));
  } else if (locationValue) {
    filteredData = electionsData.filter((v) => (v?.state || "").includes(state));
  }

  return (
    <div className="elections-container">
      {/* Action buttons */}
      <div className="election-action-btns" style={{ marginBottom: 16 }}>
        <Button
          onClick={() => {
            setLocationValue("");
            setActiveFilter("close");
          }}
          type={activeFilter === "close" ? "primary" : "default"}
          style={{ marginRight: 8 }}
        >
          Near me
        </Button>

        <Button
          onClick={() => {
            setActiveFilter("all");
            setLocationValue("");
          }}
          type={activeFilter === "all" ? "primary" : "default"}
        >
          All
        </Button>
      </div>

      {/* State search */}
      {activeFilter === "all" && (
        <div className="find-candidates-title-container">
          <h3>Find Your Elections</h3>
          <LocationInput
            id="state"
            stateOnly
            value={locationValue}
            onChange={(v) => setLocationValue(v)}
            getLangLng={findCandidatesByLocation}
          />
        </div>
      )}

      <Table
        className="elections-table"
        size="small"
        columns={tableColumns}
        // loading={isLoading}
        dataSource={filteredData}
        scroll={{ x: 768 }}
        pagination={{ defaultPageSize: 5 }}
        expandable={{
          rowExpandable: (record) => (record?.candidates?.length || 0) > 0,
          expandIcon: isRowExpanded,
          expandedRowRender: (record) => (
            <Row>
              {(record?.candidates || []).map((candidate, idx) => (
                <Col
                  key={(candidate?._id || "candidate") + "-" + idx}
                  xs={24}
                  sm={24}
                  md={12}
                  lg={8}
                  xl={6}
                  className="ant-card-grid-hoverable"
                >
                  <Link
                    to={{
                      pathname: `/candidate/${candidate?._id}`,
                      state: { data: candidate },
                    }}
                  >
                    <CandidatePreview details={candidate} buttonVisibility={false} />
                  </Link>
                </Col>
              ))}
            </Row>
          ),
        }}
      />
    </div>
  );
};

export default Elections;
